import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailVerificationBanner = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);

  // Don't show if user is verified or banner was dismissed
  if (!user || user.email_verified || dismissed) {
    return null;
  }

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/verification-token`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.already_verified) {
        toast.success(t('verify.alreadyVerified'));
        window.location.reload();
      } else if (response.data.rate_limited) {
        toast.info('Please wait a minute before requesting another email.');
      } else if (response.data.error) {
        toast.error('Failed to send verification email. Please try again.');
      } else {
        toast.success('Verification email sent! Check your inbox.');
      }
    } catch (error) {
      toast.error(t('verify.resendError'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-800/50 rounded-full flex items-center justify-center">
              <Mail className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                {t('verify.bannerTitle')}
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">
                Check your inbox and click the verification link to unlock all features.
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleResendVerification}
              disabled={resending}
              className="text-xs h-8 border-yellow-300 dark:border-yellow-700 hover:bg-yellow-100 dark:hover:bg-yellow-800/50"
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Sending...
                </>
              ) : (
                t('verify.resendEmail')
              )}
            </Button>
            <button
              onClick={() => setDismissed(true)}
              className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 p-1"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
